import { Formalite, FormalityValidator, useFormaliteService } from '@europrocurement/l2d-domain';
import React, { createContext, PropsWithChildren, useContext, useEffect, useReducer } from 'react';
import { DataProviderProps, DataState } from './types';

export type Formality = {
    id: number;
    category?: {
        id: number;
        label: string;
        iconeName: string;
    };
    label: string;
    sortOrder: number;
    formId?: number;
    combinabilities: Array<number>;
    validators: Array<FormalityValidator>;
    groupements: Array<number>;
    sirenRequired: boolean;
};

type FormalityState = DataState & {
    data: Array<Formality>;
};

const INIT_ITEMS_CTX = 'init_formalities_ctx';
export const UPDATE_ITEM_CTX = 'update_formality_ctx';

type Action =
    | { type: 'init_formalities_ctx'; payload: Array<Formality> }
    | { type: 'update_formality_ctx'; payload: Formality };

export const FormalitiesContext = createContext<FormalityState | null>(null);
export const FormalitiesDispatchContext = createContext<React.Dispatch<Action> | null>(null);

export const apiToStateTransformer = (item: Formalite): Formality => ({
    id: item.id as number,
    category: item?.categorie
        ? {
              id: item?.categorie?.id,
              label: item?.categorie?.libelle,
              iconeName: item?.categorie?.iconeName,
          }
        : undefined,
    label: item.libelle as string,
    sortOrder: item.rang as number,
    formId: item.formulaire ? item.formulaire : undefined,
    combinabilities: item?.compatibilities || [],
    validators: (item?.validators || []).map((validator) => ({
        validator: validator?.validator as number,
        default: validator?.default as boolean,
        documents: validator?.documents as boolean,
        forms: validator?.forms as boolean,
    })),
    groupements: item?.groupements || [],
    sirenRequired: item?.sirenRequired || false,
});

function formalitiesReducer(state: FormalityState, action: Action) {
    switch (action.type) {
        case INIT_ITEMS_CTX: {
            return {
                loaded: true,
                data: [...action.payload],
            };
        }
        case UPDATE_ITEM_CTX: {
            return {
                ...state,
                data: state.data.map((item) =>
                    item.id === action.payload.id ? action.payload : item,
                ),
            };
        }
        default: {
            throw new Error(`Unknown action: ${(action as Action).type}`);
        }
    }
}

export const FormalitiesProvider: React.FunctionComponent<PropsWithChildren<DataProviderProps>> =
    function ({ autoload, children }) {
        const [state, dispatch] = useReducer(formalitiesReducer, { loaded: false, data: [] });
        const { formaliteOpenSearchApi } = useFormaliteService();

        useEffect(() => {
            if (!autoload) {
                return;
            }

            formaliteOpenSearchApi
                .apiOpensearchFormalitesGetCollection({ itemsPerPage: 999 })
                .then((response) => {
                    dispatch({
                        type: INIT_ITEMS_CTX,
                        payload: response.data['hydra:member'].map(apiToStateTransformer),
                    });
                })
                .catch((e) => console.error(e));
        }, [formaliteOpenSearchApi, autoload]);

        return (
            <FormalitiesContext.Provider value={state}>
                <FormalitiesDispatchContext.Provider value={dispatch}>
                    {children}
                </FormalitiesDispatchContext.Provider>
            </FormalitiesContext.Provider>
        );
    };

export function useFormalitiesCtx() {
    const state = useContext(FormalitiesContext);

    const isDataLoaded = (): boolean => state?.loaded || false;

    const getFormalities = (): Array<Formality> => state?.data || [];

    const getFormalityById = (id: number): Formality | undefined =>
        (state?.data || []).find((formality) => formality.id === id);

    const getFormalitiesFiltered = (
        filters: Partial<Record<keyof Formality, unknown>>,
    ): Array<Formality> =>
        (state?.data || []).filter((item) =>
            Object.entries(filters).every(([key, value]) => item[key as keyof Formality] === value),
        );

    const getFormalityFiltered = (
        filters: Partial<Record<keyof Formality, unknown>>,
    ): Formality | undefined => getFormalitiesFiltered(filters)[0] || undefined;

    return {
        isDataLoaded,
        getFormalities,
        getFormalityById,
        getFormalitiesFiltered,
        getFormalityFiltered,
    };
}

export function useFormalitiesCtxDispatch() {
    return useContext(FormalitiesDispatchContext);
}
